import { openDB, type DBSchema } from 'idb';
import type { Prayer } from '@/types/prayer';
import type { BibleBookmark } from '@/types/bookmark';
import type { BibleHighlight } from '@/types/highlight';
import type { BibleProgressLastRead, BibleProgressRecentReads, BibleRecentRead } from './db';
import type {
  CompletePrayerDocument, CompleteBookmarkDocument, CompleteHighlightDocument,
  ContentEntityType, ContentPartitionV1, LocalContentEntityV1,
} from '@/types/content-lifecycle';
import {
  prayerFromCompleteDocument, bookmarkFromCompleteDocument, highlightFromCompleteDocument,
} from './content-lifecycle/contracts';
import { parseScriptureAnchor } from './scripture-anchor';
import { CANONICAL_BOOKS } from './bsb-types';
import { OfflinePrayerReadError } from './offline-prayers';

export interface OfflineOwnerSnapshot {
  ownerId: string;
  prayers: Prayer[];
  bookmarks: BibleBookmark[];
  highlights: BibleHighlight[];
  lastRead: BibleProgressLastRead | null;
  recentReads: BibleRecentRead[];
}

// This is a readonly view of the existing v10 schema. It imports db.ts TYPES
// only: a cold unverified read must never initialize/upgrade ordinary getDb().
interface ReadingDB extends DBSchema {
  'app-meta': { key: string; value: { key: string; value: unknown } };
  prayers: { key: string; value: Prayer };
  'bible-bookmarks': { key: string; value: BibleBookmark };
  'bible-highlights': { key: string; value: BibleHighlight };
  'bible-progress': { key: string; value: BibleProgressLastRead | BibleProgressRecentReads };
  'content-entities': {
    key: [string, string, string]; value: LocalContentEntityV1;
    indexes: { 'by-owner': string };
  };
  'content-partitions': { key: string; value: ContentPartitionV1 };
}

const chapterCounts = new Map(CANONICAL_BOOKS.map((book) => [book.slug, book.chapters]));
const unavailable = () => new OfflinePrayerReadError('unavailable');
const positiveInteger = (value: unknown): value is number =>
  Number.isSafeInteger(value) && (value as number) > 0;

function validChapter(bookSlug: string, chapter: number): boolean {
  const count = chapterCounts.get(bookSlug);
  return count !== undefined && positiveInteger(chapter) && chapter <= count;
}

function validProgress(row: BibleRecentRead): boolean {
  return Boolean(row && validChapter(row.bookSlug, row.chapter) &&
    Number.isFinite(row.scrollOffset) && row.scrollOffset >= 0 &&
    Number.isFinite(row.timestamp) && row.timestamp >= 0 &&
    (row.verse === undefined || positiveInteger(row.verse)));
}

function validatePrayer(prayer: Prayer): void {
  // Preserve the existing saved-prayer read contract. No authoring bounds or
  // timestamp normalization are applied retrospectively to accepted text.
  if (!prayer || typeof prayer.id !== 'string' || typeof prayer.rawText !== 'string' ||
      typeof prayer.createdAt !== 'string' || typeof prayer.updatedAt !== 'string' ||
      !['Open', 'In Progress', 'Moving', 'Answered'].includes(prayer.status) ||
      !Array.isArray(prayer.progressHistory) ||
      prayer.progressHistory.some((entry) => !entry || typeof entry.note !== 'string' || typeof entry.date !== 'string') ||
      (prayer.testimony != null && typeof prayer.testimony !== 'string') ||
      (prayer.parsed != null && typeof prayer.parsed.who !== 'string') ||
      (prayer.scriptureAnchor != null && parseScriptureAnchor(prayer.scriptureAnchor) === null)) {
    throw unavailable();
  }
}

function validateBookmark(bookmark: BibleBookmark): void {
  const anchor = bookmark && parseScriptureAnchor(bookmark.anchor);
  if (!bookmark || typeof bookmark.id !== 'string' || bookmark.id === '' ||
      typeof bookmark.note !== 'string' || typeof bookmark.createdAt !== 'string' ||
      typeof bookmark.updatedAt !== 'string' || !anchor ||
      bookmark.bookSlug !== anchor.bookSlug || bookmark.chapter !== anchor.chapter ||
      !validChapter(bookmark.bookSlug, bookmark.chapter) ||
      (bookmark.deletedAt != null && typeof bookmark.deletedAt !== 'string')) {
    throw unavailable();
  }
}

function validateHighlight(highlight: BibleHighlight): void {
  if (!highlight || typeof highlight.id !== 'string' ||
      !validChapter(highlight.bookSlug, highlight.chapter) || !positiveInteger(highlight.verse) ||
      highlight.id !== `${highlight.bookSlug}:${highlight.chapter}:${highlight.verse}` ||
      !['promise', 'comfort', 'command', 'conviction', 'praise', 'character', 'plain'].includes(highlight.family) ||
      typeof highlight.reference !== 'string' || typeof highlight.createdAt !== 'string' ||
      typeof highlight.updatedAt !== 'string' ||
      (highlight.sublabel != null && typeof highlight.sublabel !== 'string') ||
      (highlight.personWho != null && typeof highlight.personWho !== 'string') ||
      (highlight.fulfilledAt != null && typeof highlight.fulfilledAt !== 'string') ||
      (highlight.deletedAt != null && typeof highlight.deletedAt !== 'string')) {
    throw unavailable();
  }
}

/**
 * Caller holds the existing shared owner lease and must recheck its retained
 * session/generation before AND after this promise. This result confers no
 * write authority. A failed/missing binding returns no private data; callers
 * may still offer the public cached BSB reader.
 */
export async function readOfflineOwnerSnapshot(ownerId: string): Promise<OfflineOwnerSnapshot> {
  let db;
  try {
    db = await openDB<ReadingDB>('adia-db', undefined, {
      upgrade(_database, _oldVersion, _newVersion, transaction) {
        void transaction.done.catch(() => undefined);
        transaction.abort();
      },
    });
  } catch {
    throw unavailable();
  }
  try {
    if (db.version !== 10) throw unavailable();
    const names = ['app-meta', 'prayers', 'bible-bookmarks', 'bible-highlights',
      'bible-progress', 'content-entities', 'content-partitions'] as const;
    if (names.some((name) => !db.objectStoreNames.contains(name))) throw unavailable();
    const tx = db.transaction(names, 'readonly');
    const [binding, cleanup, partition, entities, legacyPrayers, legacyBookmarks,
      legacyHighlights, last, recent] = await Promise.all([
      tx.objectStore('app-meta').get('content-owner'),
      tx.objectStore('app-meta').get('owner-adoption-cleanup'),
      tx.objectStore('content-partitions').get(ownerId),
      tx.objectStore('content-entities').index('by-owner').getAll(ownerId),
      tx.objectStore('prayers').getAll(),
      tx.objectStore('bible-bookmarks').getAll(),
      tx.objectStore('bible-highlights').getAll(),
      tx.objectStore('bible-progress').get(`${ownerId}:last-read`),
      tx.objectStore('bible-progress').get(`${ownerId}:recent-reads`),
      // Observe transaction rejection even when a request rejects first.
      tx.done,
    ]);
    if (binding?.value !== ownerId || cleanup !== undefined ||
        (partition && partition.ownerId !== ownerId) ||
        entities.some((entity) => !entity || entity.ownerId !== ownerId)) {
      throw new OfflinePrayerReadError('owner-changed');
    }
    if (partition?.quarantineReason ||
        (partition?.legacyBootstrapEntityTypes !== undefined &&
          !Array.isArray(partition.legacyBootstrapEntityTypes))) throw unavailable();
    const initialized = (type: ContentEntityType) => partition?.legacyBootstrapComplete === true ||
      partition?.legacyBootstrapEntityTypes?.includes(type) === true;
    const ofType = (type: ContentEntityType) => entities.filter((entity) => entity.entityType === type);
    for (const entity of entities) {
      if (!['prayer', 'bookmark', 'highlight'].includes(entity.entityType) ||
          typeof entity.entityId !== 'string' || entity.entityId === '' ||
          !['clean', 'dirty', 'tombstoned', 'conflicted', 'held', 'quarantined'].includes(entity.state) ||
          entity.state === 'quarantined' ||
          (entity.visibleDocument !== null && (entity.visibleDocument?.id !== entity.entityId ||
            entity.visibleDocument.deletedAt !== null || entity.state === 'tombstoned'))) {
        throw unavailable();
      }
    }

    const prayerEntities = ofType('prayer');
    const prayerIds = new Set(prayerEntities.map((entity) => entity.entityId));
    const legacyById = new Map(legacyPrayers.map((prayer) => [prayer.id, prayer]));
    const prayers = !initialized('prayer') && prayerEntities.length === 0 ? legacyPrayers : [
      ...prayerEntities.flatMap((entity) => entity.visibleDocument === null ? [] : [
        prayerFromCompleteDocument(entity.visibleDocument as CompletePrayerDocument, legacyById.get(entity.entityId)),
      ]),
      // Pending-transcription local-only prayers remain readable without
      // hydrating audio, recovery hooks, or any background worker.
      ...legacyPrayers.filter((prayer) => Boolean(prayer.pendingTranscriptionId) && !prayerIds.has(prayer.id)),
    ];
    const bookmarkEntities = ofType('bookmark');
    const bookmarks = !initialized('bookmark') && bookmarkEntities.length === 0 ? legacyBookmarks :
      bookmarkEntities.flatMap((entity) => entity.visibleDocument === null ? [] : [
        bookmarkFromCompleteDocument(entity.visibleDocument as CompleteBookmarkDocument),
      ]);
    const highlightEntities = ofType('highlight');
    const highlights = !initialized('highlight') && highlightEntities.length === 0 ? legacyHighlights :
      highlightEntities.flatMap((entity) => entity.visibleDocument === null ? [] : [
        highlightFromCompleteDocument(entity.visibleDocument as CompleteHighlightDocument),
      ]);
    // Count all lifecycle entities, including tombstones, above. An empty
    // live projection must never cause a deleted legacy row to reappear.
    prayers.forEach(validatePrayer);
    bookmarks.forEach(validateBookmark);
    highlights.forEach(validateHighlight);

    let lastRead: BibleProgressLastRead | null = null;
    let recentReads: BibleRecentRead[] = [];
    if (last !== undefined) {
      if (last.ownerId !== ownerId || last.key !== `${ownerId}:last-read` ||
          !('scrollOffset' in last) || !validProgress(last)) throw unavailable();
      lastRead = last;
    }
    if (recent !== undefined) {
      if (recent.ownerId !== ownerId || recent.key !== `${ownerId}:recent-reads` ||
          !('entries' in recent) || !Array.isArray(recent.entries) ||
          recent.entries.some((entry) => !validProgress(entry))) throw unavailable();
      recentReads = recent.entries;
    }
    return {
      ownerId,
      prayers: prayers.sort((a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id)),
      bookmarks: bookmarks.filter((row) => !row.deletedAt),
      highlights: highlights.filter((row) => !row.deletedAt),
      lastRead, recentReads,
    };
  } catch (error) {
    if (error instanceof OfflinePrayerReadError) throw error;
    throw unavailable();
  } finally {
    db.close();
  }
}
